/**
 * LMS SSO deep-link redirect helpers.
 *
 * When an approver opens an LMS approval email while logged out, the LMS SPA
 * bounces to the app-side `/lms-sso` bridge. The bridge remembers where the
 * approver was headed and replays it after login, so they land on the LMS
 * approval detail page instead of the dashboard.
 *
 * The intended path is stashed in `sessionStorage` (not `localStorage`) on
 * purpose: the app's 401 interceptor calls `localStorage.clear()`, but the
 * stash must survive that (stale-token handoff) and also survive the full-page
 * round-trip to an external Microsoft / SAML / AD auth service (same tab).
 */

export const POST_LOGIN_REDIRECT_KEY = 'post_login_redirect';

/**
 * Open-redirect guard — accept only relative, in-app LMS paths.
 * Rejects absolute URLs, protocol-relative `//host`, and non-LMS paths.
 * These values are only ever fed to `router.push()` (SPA-internal), never to
 * `window.location`, so this purely guards against off-origin navigation.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function safeLmsRedirect(value) {
    return typeof value === 'string'
        && value.charAt(0) === '/'            // must be relative
        && value.charAt(1) !== '/'            // reject //evil.com
        && /^\/lms(\/|-sso|\?|$)/.test(value); // /lms/... or /lms-sso...
}

/**
 * Broader post-login guard — accept the same-origin, in-app deep-links an
 * unauthenticated user can be bounced away from and should be returned to after
 * login. Covers the LMS paths (safeLmsRedirect) PLUS the campaign / announcement
 * / policy approver "Review & Approve" preview pages and the creator/approver
 * workspace pages, e.g.
 *   /campaigns/preview/<id>?from=/campaigns/approver-workspace
 *   /campaigns/creator-workspace  (creator "Review Campaign" result email)
 * Rejects absolute URLs and protocol-relative `//host`. Values are only ever fed
 * to router.push() (SPA-internal), never to window.location.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function safePostLoginRedirect(value) {
    return typeof value === 'string'
        && value.charAt(0) === '/'            // must be relative
        && value.charAt(1) !== '/'            // reject //evil.com
        && (
            /^\/lms(\/|-sso|\?|$)/.test(value)                                                        // LMS deep-links + SSO bridge
            || /^\/(campaigns|announcements|policy)\/preview(\/|\?|$)/.test(value)                    // approver review deep-links (campaign preview)
            || /^\/(campaigns|announcements|policy)\/(creator|approver)-workspace(\/|\?|$)/.test(value) // creator/approver workspace deep-links
        );
}

/**
 * Read + clear the stashed post-login redirect. Returns a safe path or null.
 * @returns {string|null}
 */
export function popPostLoginRedirect() {
    var stored = null;
    try {
        stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
        if (stored) {
            sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        }
    } catch (e) {
        return null;
    }
    return safePostLoginRedirect(stored) ? stored : null;
}



// WEBPACK FOOTER //
// ./src/utils/lmsRedirect.js